import { useEffect, useMemo, useState, useCallback } from 'react';
import { Users, Calendar as CalIcon, AlertTriangle, ChevronLeft, ChevronRight, Download, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../utils/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const fmtHrs = (h) => h === null || h === undefined ? '—' : `${h.toFixed(2)}h`;
const toIST = (d) => {
  const ist = new Date(d.getTime() + (5.5 * 60 - d.getTimezoneOffset()) * 60000);
  return ist.toISOString().slice(0, 10);
};
const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const offset = day === 0 ? 6 : day - 1; // Monday-start
  d.setDate(d.getDate() - offset);
  return toIST(d);
};
const endOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const offset = day === 0 ? 0 : 7 - day;
  d.setDate(d.getDate() + offset);
  return toIST(d);
};

// Color a utilization bar — green ≤80, amber 80-100, red >100
const utilColor = (pct) => {
  if (pct >= 100) return 'bg-red-500';
  if (pct >= 80) return 'bg-amber-500';
  if (pct > 0) return 'bg-emerald-500';
  return 'bg-slate-300';
};
const utilText = (pct) => {
  if (pct >= 100) return 'text-red-700';
  if (pct >= 80) return 'text-amber-700';
  if (pct > 0) return 'text-emerald-700';
  return 'text-slate-500';
};
const utilLabel = (pct) => {
  if (pct >= 100) return 'Over-allocated';
  if (pct >= 80) return 'Near capacity';
  if (pct > 0) return 'Healthy';
  return 'Idle';
};

export default function CapacityPlanner() {
  const today = useMemo(() => new Date(), []);
  const [fromDate, setFromDate] = useState(startOfWeek(today));
  const [toDate, setToDate] = useState(endOfWeek(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [drill, setDrill] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/analytics/capacity', { params: { from_date: fromDate, to_date: toDate } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load capacity report');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleRow = async (userId) => {
    setExpanded(prev => ({ ...prev, [userId]: !prev[userId] }));
    if (!drill[userId]) {
      try {
        const res = await api.get(`/analytics/capacity/${userId}`, { params: { from_date: fromDate, to_date: toDate } });
        setDrill(prev => ({ ...prev, [userId]: res.data.tasks || [] }));
      } catch (e) { /* noop */ }
    }
  };

  const prevWeek = () => {
    const d = new Date(fromDate); d.setDate(d.getDate() - 7);
    setFromDate(startOfWeek(d)); setToDate(endOfWeek(d));
  };
  const nextWeek = () => {
    const d = new Date(fromDate); d.setDate(d.getDate() + 7);
    setFromDate(startOfWeek(d)); setToDate(endOfWeek(d));
  };
  const thisWeek = () => { setFromDate(startOfWeek(today)); setToDate(endOfWeek(today)); };

  const exportCsv = () => {
    if (!data) return;
    const head = ['Employee', 'Role', 'Weekly cap (h)', 'Working days', 'Leave days', 'Capacity (h)', 'Committed (h)', 'Logged (h)', 'Available (h)', 'Utilization %', 'Open tasks', 'Without estimate'];
    const lines = data.users.map(u => [u.user_name, u.role, u.weekly_capacity_hours, u.working_days, u.leave_days, u.capacity_hours, u.committed_hours, u.logged_hours, u.available_hours, u.utilization_pct, u.open_task_count, u.tasks_without_estimate].join(','));
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `capacity_${fromDate}_to_${toDate}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const agg = useMemo(() => {
    if (!data) return null;
    const a = { capacity: 0, committed: 0, logged: 0, available: 0, over: 0, near: 0, healthy: 0, idle: 0, no_est: 0 };
    for (const u of data.users) {
      a.capacity += u.capacity_hours;
      a.committed += u.committed_hours;
      a.logged += u.logged_hours;
      a.available += u.available_hours;
      a.no_est += u.tasks_without_estimate;
      if (u.utilization_pct >= 100) a.over++;
      else if (u.utilization_pct >= 80) a.near++;
      else if (u.utilization_pct > 0) a.healthy++;
      else a.idle++;
    }
    return a;
  }, [data]);

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="capacity-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" data-testid="capacity-heading">
            <Users className="h-7 w-7 text-[#5B6FD6]" /> Capacity Planner
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            See who's over-allocated and who has room. Capacity = working days × (weekly capacity ÷ 5). Committed = sum of estimates on open tasks with deadlines in this range.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data?.users?.length} data-testid="capacity-export-btn">
          <Download className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Range picker */}
      <Card className="border-slate-200 p-3 bg-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={prevWeek} data-testid="capacity-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="text-base md:text-lg font-semibold text-slate-800 min-w-[200px] text-center" data-testid="capacity-range">
              {fromDate} → {toDate} <span className="text-xs text-slate-400 font-normal">({data?.working_days ?? '–'} working days)</span>
            </div>
            <Button variant="outline" size="icon" onClick={nextWeek} data-testid="capacity-next"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" className="text-xs" onClick={thisWeek}>This week</Button>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <CalIcon className="h-3.5 w-3.5 text-slate-400" />
            <Input type="date" value={fromDate} max={toDate} onChange={e => setFromDate(e.target.value)} className="bg-white w-36" data-testid="capacity-from" />
            <span className="text-slate-400">→</span>
            <Input type="date" value={toDate} min={fromDate} onChange={e => setToDate(e.target.value)} className="bg-white w-36" data-testid="capacity-to" />
          </div>
        </div>
      </Card>

      {/* Aggregate tiles */}
      {agg && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="capacity-agg">
          <Tile label="Total capacity" value={`${agg.capacity.toFixed(0)}h`} />
          <Tile label="Total committed" value={`${agg.committed.toFixed(0)}h`} />
          <Tile label="Available headroom" value={`${agg.available.toFixed(0)}h`} color={agg.available > 0 ? 'text-emerald-700' : 'text-slate-500'} bg={agg.available > 0 ? 'bg-emerald-50' : 'bg-slate-50'} />
          <Tile label="Over-allocated" value={agg.over} color={agg.over > 0 ? 'text-red-700' : 'text-slate-500'} bg={agg.over > 0 ? 'bg-red-50' : 'bg-slate-50'} />
          <Tile label="Tasks w/o estimate" value={agg.no_est} color={agg.no_est > 0 ? 'text-amber-700' : 'text-slate-500'} bg={agg.no_est > 0 ? 'bg-amber-50' : 'bg-slate-50'} />
        </div>
      )}

      {/* Banner if visibility gap */}
      {agg?.no_est > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="no-estimate-banner">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-semibold">{agg.no_est} open task{agg.no_est === 1 ? '' : 's'} in this range have no estimate.</span> Capacity planning will under-report commitment until estimates are filled in. Expand any row below to spot them, or ask assignees to set estimates from the task detail page.
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="capacity-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold w-8"></th>
                <th className="text-left px-4 py-3 font-semibold">Employee</th>
                <th className="text-right px-4 py-3 font-semibold">Capacity</th>
                <th className="text-right px-4 py-3 font-semibold">Committed</th>
                <th className="text-left px-4 py-3 font-semibold w-64">Utilization</th>
                <th className="text-right px-4 py-3 font-semibold">Available</th>
                <th className="text-right px-4 py-3 font-semibold">Logged</th>
                <th className="text-right px-4 py-3 font-semibold">Open</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={8} className="text-center text-slate-400 py-10">Loading…</td></tr>
              ) : !data?.users?.length ? (
                <tr><td colSpan={8} className="text-center text-slate-400 py-10">No employees match.</td></tr>
              ) : data.users.map(u => (
                <>
                <tr key={u.user_id} className={`hover:bg-slate-50/50 ${u.utilization_pct >= 100 ? 'bg-red-50/30' : ''}`} data-testid={`capacity-row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleRow(u.user_id)} className="text-slate-400 hover:text-slate-700" data-testid={`capacity-toggle-${u.user_id}`} title="Show tasks">
                      {expanded[u.user_id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#5B6FD6] to-[#10B981] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {u.user_name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{u.user_name}</p>
                        <p className="text-[10px] text-slate-400 capitalize">
                          {u.role} · {u.weekly_capacity_hours}h/wk
                          {u.leave_days > 0 && <span className="text-amber-600 ml-1">· {u.leave_days} leave</span>}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{fmtHrs(u.capacity_hours)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{fmtHrs(u.committed_hours)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden min-w-[100px]">
                        <div className={`h-full ${utilColor(u.utilization_pct)} transition-all`} style={{ width: `${Math.min(100, u.utilization_pct)}%` }} />
                      </div>
                      <span className={`text-xs font-mono font-bold min-w-[50px] text-right ${utilText(u.utilization_pct)}`} data-testid={`capacity-util-${u.user_id}`}>
                        {u.utilization_pct}%
                      </span>
                    </div>
                    <p className={`text-[10px] mt-0.5 ${utilText(u.utilization_pct)}`}>{utilLabel(u.utilization_pct)}</p>
                  </td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${u.available_hours > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>
                    {fmtHrs(u.available_hours)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-500">{fmtHrs(u.logged_hours)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-mono text-slate-700">{u.open_task_count}</span>
                    {u.tasks_without_estimate > 0 && (
                      <span className="ml-1 text-[10px] text-amber-700 font-semibold" title={`${u.tasks_without_estimate} task(s) without estimate`}>
                        ({u.tasks_without_estimate}!)
                      </span>
                    )}
                  </td>
                </tr>
                {expanded[u.user_id] && (
                  <tr key={`${u.user_id}-drill`} className="bg-slate-50/50">
                    <td colSpan={8} className="px-12 py-3">
                      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Open tasks contributing this range</p>
                      {!drill[u.user_id] ? (
                        <p className="text-xs text-slate-400">Loading tasks…</p>
                      ) : drill[u.user_id].length === 0 ? (
                        <p className="text-xs text-slate-400">No open tasks with deadlines in this range.</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {drill[u.user_id].map(t => (
                            <li key={t.id} className="flex items-center justify-between text-xs gap-2">
                              <Link to={`/tasks/${t.id}`} className="text-[#5B6FD6] hover:text-[#4338CA] truncate flex-1">
                                {t.title} <span className="text-slate-400">· {t.client_name} · {t.service_name}</span>
                              </Link>
                              <span className="text-slate-500 font-mono">
                                {t.estimate_hours
                                  ? <span className="text-slate-700">{t.estimate_hours}h</span>
                                  : <span className="text-amber-700 inline-flex items-center gap-0.5"><Info className="h-3 w-3" /> no estimate</span>}
                                <span className="text-slate-400 ml-2">due {t.deadline?.slice(0,10)}</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

const Tile = ({ label, value, color = 'text-slate-700', bg = 'bg-slate-50' }) => (
  <div className={`rounded-lg px-3 py-2.5 ${bg}`}>
    <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label}</p>
    <p className={`text-lg font-mono font-semibold ${color}`}>{value}</p>
  </div>
);
